import React, { useState, useRef, useEffect, useCallback } from "react";
import logo from "../../assets/images/Auth/tt-logo.png";
import { useFetchCreateClientTestimonialMutation } from "../../slice/logdetailSlice";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────
   Face detection uses the browser-native FaceDetector API
   (available in Chromium / Edge). Falls back gracefully when
   the API is not available.
───────────────────────────────────────────────────────────── */

function Testimonial() {
  /* ── navigation ─────────────────────────────────────────── */
  const [step, setStep] = useState(1);
  const [step1Done, setStep1Done] = useState(false); // unlocks step 2 click
  const [createClientTestimonial] = useFetchCreateClientTestimonialMutation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const project = searchParams.get("project");
  /* ── step 1 ─────────────────────────────────────────────── */
  const [userName, setUserName] = useState("");
  const [step1Errors, setStep1Errors] = useState({});

  /* ── step 2 – recording ─────────────────────────────────── */
  const [recordingState, setRecordingState] = useState("idle");
  // idle | recording | paused | stopped
  const [videoURL, setVideoURL] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /* ── face detection ─────────────────────────────────────── */
  const [faceDetectorSupported, setFaceDetectorSupported] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);
  const faceDetectionRef = useRef(null); // animation-frame id
  const faceDetectorInstanceRef = useRef(null);

  /* ── refs ───────────────────────────────────────────────── */
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordedBlobRef = useRef(null);

  /* ── cleanup on unmount ─────────────────────────────────── */
  useEffect(() => {
    return () => {
      stopFaceDetection();
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── check FaceDetector support on mount ────────────────── */
  useEffect(() => {
    const supported = "FaceDetector" in window;
    setFaceDetectorSupported(supported);
    if (supported) {
      // eslint-disable-next-line no-undef
      faceDetectorInstanceRef.current = new FaceDetector({
        fastMode: true,
        maxDetectedFaces: 1,
      });
    }
  }, []);

  /* ════════════════════════════════════════════════════════════
     STEP 1 helpers
  ═══════════════════════════════════════════════════════════ */
  const validateStep1 = () => {
    const errs = {};
    if (!userName.trim()) errs.userName = "Name is required";
    // if (starRating === 0) errs.starRating = "Please select a star rating";
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep1();
    if (Object.keys(errs).length > 0) {
      setStep1Errors(errs);
      return;
    }
    setStep1Errors({});
    setStep1Done(true);
    setStep(2);
  };

  /* navigate via step indicator */
  const goToStep = (s) => {
    if (s === 1) {
      // Going back to step 1 – stop camera / detection
      stopFaceDetection();
      stopRecording();
      stopCamera();
      setRecordingState("idle");
      setVideoURL(null);
      recordedBlobRef.current = null;
      chunksRef.current = [];
      if (videoRef.current) {
        videoRef.current.src = "";
        videoRef.current.srcObject = null;
        videoRef.current.controls = false;
      }
      setStep(1);
    } else if (s === 2 && step1Done) {
      setStep(2);
    }
  };

  /* ════════════════════════════════════════════════════════════
     FACE DETECTION helpers
  ═══════════════════════════════════════════════════════════ */
  const stopFaceDetection = () => {
    if (faceDetectionRef.current) {
      cancelAnimationFrame(faceDetectionRef.current);
      faceDetectionRef.current = null;
    }
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setFaceDetected(false);
  };

  const runFaceDetection = useCallback(async () => {
    const detector = faceDetectorInstanceRef.current;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!detector || !video || !canvas) return;
    if (video.readyState < 2) {
      faceDetectionRef.current = requestAnimationFrame(runFaceDetection);
      return;
    }

    try {
      const faces = await detector.detect(video);
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (faces.length > 0) {
        setFaceDetected(true);
        faces.forEach(({ boundingBox }) => {
          ctx.strokeStyle = "rgb(2,178,152)";
          ctx.lineWidth = 3;
          ctx.strokeRect(
            boundingBox.x,
            boundingBox.y,
            boundingBox.width,
            boundingBox.height,
          );
        });
      } else {
        setFaceDetected(false);
      }
    } catch (_) {
      // detection error – skip frame
    }

    faceDetectionRef.current = requestAnimationFrame(runFaceDetection);
  }, []);

  /* ════════════════════════════════════════════════════════════
     CAMERA helpers
  ═══════════════════════════════════════════════════════════ */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.controls = false;
        await videoRef.current.play();

        // Sync canvas size
        if (canvasRef.current) {
          canvasRef.current.width = videoRef.current.videoWidth || 640;
          canvasRef.current.height = videoRef.current.videoHeight || 360;
        }
      }

      // Start face detection if supported
      if (faceDetectorSupported) {
        faceDetectionRef.current = requestAnimationFrame(runFaceDetection);
      }
      return stream;
    } catch (err) {
      // console.error("Camera error:", err);
      alert("Could not access camera/microphone. Please allow permissions.");
      return null;
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  /* ════════════════════════════════════════════════════════════
     RECORDING helpers
  ═══════════════════════════════════════════════════════════ */
  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleStart = async () => {
    setVideoURL(null);
    recordedBlobRef.current = null;
    chunksRef.current = [];

    const stream = await startCamera();
    if (!stream) return;

    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      stopFaceDetection();

      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      recordedBlobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setVideoURL(url);

      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = url;
        videoRef.current.muted = false;
        videoRef.current.controls = true;
      }
      stopCamera();
    };

    recorder.start();
    setRecordingState("recording");
  };

  const handleStop = () => {
    stopRecording();
    setRecordingState("stopped");
  };

  const handlePauseResume = () => {
    if (!mediaRecorderRef.current) return;
    if (mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setRecordingState("paused");
    } else if (mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setRecordingState("recording");
    }
  };

  const handleDelete = () => {
    stopFaceDetection();
    stopRecording();
    stopCamera();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    recordedBlobRef.current = null;
    setVideoURL(null);
    setRecordingState("idle");

    if (videoRef.current) {
      videoRef.current.src = "";
      videoRef.current.srcObject = null;
      videoRef.current.controls = false;
    }
  };

  const handleRegenerate = () => {
    handleDelete();
    setTimeout(() => handleStart(), 300);
  };

  /* ════════════════════════════════════════════════════════════
     SUBMIT
  ═══════════════════════════════════════════════════════════ */
  const handleSubmit = async () => {
    setSubmitting(true);
    const file = recordedBlobRef.current
      ? new File([recordedBlobRef.current], "testimonial.webm", {
          type: "video/webm",
        })
      : null;
    if (!file) {
      toast.error("Video file is missing");
      return;
    }
    // console.log("=== Testimonial Submission ===");
    // console.log("User Name  :", userName);
    // console.log("Video File :", file);
    try {
      const formData = new FormData();
      formData.append("name", userName);
      formData.append("testimonial_video", file);
      formData.append("token", token || "");
      formData.append("project_id", encodeURIComponent(project) || "");
      // console.log("formData", formData);
      // formData.forEach((value, key) => {
      //   console.log(`${key}:`, value);
      // });
      const response = await createClientTestimonial({
        url: "/testimonial/submit",
        body: formData,
      }).unwrap();
      // console.log("response", response);
      if (response) {
        handleDelete();
        setStep(3); // Show Thank You screen
        toast.success(response?.data?.message || response?.data?.data?.message);
      }
    } catch (error) {
      toast.error(error?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */
  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "var(--main-bg-color)" }}
    >
      <div className="w-100" style={{ maxWidth: 500, padding: "32px 24px" }}>
        {/* Logo */}
        <div className="text-center mb-4">
          <img src={logo} alt="logo" />
        </div>

        {/* ── Step indicator ── */}
        <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
          {[1, 2, 3].map((s) => {
            const isActive = step === s;
            const isDone = s < step;
            const isClickable = (s === 1 || (s === 2 && step1Done)) && step < 3;
            if (s === 3 && step < 3) return null; // Only show step 3 icon when we are ON step 3
            return (
              <React.Fragment key={s}>
                <div
                  onClick={() => isClickable && goToStep(s)}
                  title={
                    s === 2 && !step1Done
                      ? "Complete Step 1 first"
                      : `Step ${s}`
                  }
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    backgroundColor:
                      isActive || isDone
                        ? "rgb(2,178,152)"
                        : "var(--border-color)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                    cursor: isClickable ? "pointer" : "default",
                    transition: "background-color 0.2s",
                    opacity: isClickable || isActive || isDone ? 1 : 0.5,
                  }}
                >
                  {isDone ? "✓" : s}
                </div>
                {s < 3 && s !== (step === 3 ? 3 : 2) && (
                  <div
                    style={{
                      height: 2,
                      width: 60,
                      backgroundColor: "var(--border-color)",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ─────────────── STEP 1 ─────────────── */}
        {step === 1 && (
          <div>
            <h5 className="mb-4" style={{ color: "var(--text-color)" }}>
              Your Details
            </h5>

            <div className="mb-3">
              <label className="form-label label_grey">Name *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter your name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                style={{
                  padding: "12px",
                  background: "var(--cell-bg-color)",
                  color: "var(--text-color)",
                  border: "1px solid var(--border-color)",
                  borderRadius: 5,
                }}
              />
              {step1Errors.userName && (
                <small className="text-danger">{step1Errors.userName}</small>
              )}
            </div>

            {/* <div className="mb-4">
              <label className="form-label label_grey">Star Rating *</label>
              <div className="d-flex gap-1" style={{ fontSize: 34 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    style={{
                      cursor: "pointer",
                      color:
                        star <= (hoveredStar || starRating)
                          ? "#f5c518"
                          : "var(--border-color)",
                      transition: "color 0.1s",
                      userSelect: "none",
                    }}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(0)}
                    onClick={() => setStarRating(star)}
                  >
                    ★
                  </span>
                ))}
              </div>
              {step1Errors.starRating && (
                <small className="text-danger">{step1Errors.starRating}</small>
              )}
            </div> */}

            <div className="d-grid">
              <button className="auth_btn" onClick={handleNext}>
                Next
              </button>
            </div>
          </div>
        )}

        {/* ─────────────── STEP 2 ─────────────── */}
        {step === 2 && (
          <div>
            <h5 className="mb-3" style={{ color: "var(--text-color)" }}>
              Record Your Video
            </h5>

            {/* Face detection status badge */}
            {/* {faceDetectorSupported === false && (
              <div
                className="mb-2"
                style={{
                  fontSize: 12,
                  color: "#888",
                  textAlign: "center",
                }}
              >
                Face detection not supported in this browser (use Chrome/Edge)
              </div>
            )} */}
            {faceDetectorSupported &&
              (recordingState === "recording" ||
                recordingState === "paused") && (
                <div
                  className="mb-2 text-center"
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: faceDetected ? "rgb(2,178,152)" : "#e74c3c",
                  }}
                >
                  {faceDetected
                    ? "✔ Face detected"
                    : "⚠ No face detected — point camera at your face"}
                </div>
              )}

            {/* Video + canvas overlay */}
            <div
              className="mb-3"
              style={{
                position: "relative",
                background: "#111",
                borderRadius: 8,
                overflow: "hidden",
                aspectRatio: "16/9",
              }}
            >
              <video
                ref={videoRef}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
                playsInline
              />

              {/* Canvas drawn on top of video for bounding box */}
              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none",
                }}
              />

              {/* Idle placeholder */}
              {recordingState === "idle" && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#888",
                    fontSize: 13,
                    pointerEvents: "none",
                  }}
                >
                  Press Start to begin recording
                </div>
              )}

              {/* Recording indicator */}
              {recordingState === "recording" && (
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "rgba(0,0,0,0.55)",
                    borderRadius: 20,
                    padding: "3px 10px",
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#e74c3c",
                      animation: "blink 1s infinite",
                    }}
                  />
                  <span
                    style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}
                  >
                    REC
                  </span>
                </div>
              )}

              {recordingState === "paused" && (
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    background: "rgba(0,0,0,0.55)",
                    borderRadius: 20,
                    padding: "3px 10px",
                    color: "#f5c518",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  ⏸ PAUSED
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="d-flex flex-wrap gap-2 mb-4">
              {recordingState === "idle" && (
                <button className="add_btn" onClick={handleStart}>
                  Start
                </button>
              )}

              {(recordingState === "recording" ||
                recordingState === "paused") && (
                <button className="cancel_btn" onClick={handleStop}>
                  Stop
                </button>
              )}

              {(recordingState === "recording" ||
                recordingState === "paused") && (
                <button className="addmore_btn" onClick={handlePauseResume}>
                  {recordingState === "paused" ? "Resume" : "Pause"}
                </button>
              )}

              {recordingState === "stopped" && (
                <button className="cancel_btn" onClick={handleDelete}>
                  Delete
                </button>
              )}

              {recordingState === "stopped" && (
                <button className="addmore_btn" onClick={handleRegenerate}>
                  Re-record
                </button>
              )}
            </div>

            {/* Submit */}
            <div className="d-grid">
              <button
                className="auth_btn"
                onClick={handleSubmit}
                disabled={submitting || recordingState !== "stopped"}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        )}

        {/* ─────────────── STEP 3 (Thank You) ─────────────── */}
        {step === 3 && (
          <div className="text-center py-4 animate__animated animate__fadeIn">
            <div className="mb-4">
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "rgb(2,178,152)",
                  color: "white",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 40,
                  boxShadow: "0 4px 15px rgba(2,178,152,0.3)"
                }}
              >
                ✓
              </div>
            </div>
            <h2 className="mb-2" style={{ color: "var(--text-color)", fontWeight: 700 }}>
              Thank You!
            </h2>
            <p className="mb-4 text-muted" style={{ fontSize: 16 }}>
              Your testimonial video has been successfully uploaded.
            </p>
            <div
              className="p-4 mx-auto"
              style={{
                maxWidth: 400,
                background: "var(--cell-bg-color)",
                borderRadius: 16,
                border: "1px solid var(--border-color)",
                textAlign: "left",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
              }}
            >
              <div className="mb-3 d-flex justify-content-between align-items-center">
                <span className="text-muted" style={{ fontSize: 14 }}>Name:</span>
                <strong style={{ color: "var(--text-color)" }}>{userName}</strong>
              </div>
              
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted" style={{ fontSize: 14 }}>Date:</span>
                <strong style={{ color: "var(--text-color)" }}>{new Date().toLocaleDateString('en-GB').split('/').join('-')}</strong>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Blink keyframe for REC dot */}
      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default Testimonial;
